import React, { lazy, Suspense } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import { useAvailableActions } from '../hooks/useAvailableActions';
import { useSelector } from 'react-redux';

import DashboardLayout from '../layouts/dashboard';
import CreateProfile from '../pages/profiles/create-new-profile';
import EditProfile from '../pages/profiles/edit-profile';
import ProfilesListSkillkoTableInductionComplete from '../pages/profiles/import-skillko/import-skillko-page-induction-complete';
import CreateSkillkoProfile from '../pages/profiles/import-skillko/create-profile-from-skillko';
import CreateSitePassProfile from '../pages/profiles/import-sitepass/create-profile-from-sitepass';
import SuspenseDiv from '../layouts/SuspenseDiv';
import SkillkoSettingsPage from '../pages/sites/skillko-settings-page';
import ProcoreSettingsPage from '../pages/sites/procore-settings';
import FaceScannerPage from '../pages/sites/face-scanner-page';
import AdministrativeSiteSettings from '../pages/sites/administrative-site-settings';
import SitePipelineComparePage from '../pages/sites/site-pipeline-compare-page';
import AccessLevelDesigner from '../pages/sites/access-level-designer';
import AccessLevelBuilder from '../pages/sites/access-level-builder';
import ImportSyncSettings from '../pages/sites/import-sync-settings';
import CombineContractingCompaniesPage from '../pages/contracting-companies/combine';
import CombineJobTitlesPage from '../pages/job-titles/combine';

export const IndexPage = lazy(() => import('../pages/app/app'));
export const SitesPage = lazy(() => import('../pages/sites/sites'));
export const SitePage = lazy(() => import('../pages/site/site'));
export const ProfilesPage = lazy(() => import('../pages/profiles/profiles'));
export const TenantProfilesPage = lazy(() => import('../pages/profiles/tenant-profiles'));
export const TenantProfileDetailsPage = lazy(() => import('../pages/profiles/tenant-profile-details'));
export const UsersPage = lazy(() => import('../pages/users/users'));
export const EventsPage = lazy(() => import('../pages/reports/events/events'));
export const UserTimeAndAttendancePage = lazy(
  () => import('../pages/reports/time-and-attendance/user-time-and-attendance/user-time-and-attendance'),
);
export const CompanyTimeAndAttendancePage = lazy(
  () => import('../pages/reports/time-and-attendance/company-time-and-attendance/company-time-and-attendance'),
);
export const ReportingHubPage = lazy(() => import('../pages/reports/reporting-hub/reporting-hub'));
export const Page404 = lazy(() => import('../pages/error/notFoundView'));
export const OverviewPage = lazy(() => import('../pages/overview/overview'));
export const AccountSettingsPage = lazy(() => import('../pages/account/account-settings'));
export const SkillkoHealthCheckPage = lazy(() => import('../pages/account/skillko-health-check'));
export const AuditTrailPage = lazy(() => import('../pages/account/audit-trail'));
export const EventGeneratorPage = lazy(() => import('../pages/event-generator/event-generator'));
export const ProcoreExplorerPage = lazy(() => import('../pages/procore/procore-explorer'));
export const VisitorsPage = lazy(() => import('../pages/visitors/visitors'));
export const VisitorWizardPage = lazy(() => import('../pages/visitors/visitor-wizard'));

// ----------------------------------------------------------------------

export default function Router() {
  const {
    canReadProfiles,
    canReadSites,
    canReadEvents,
    canCreateProfile,
    canReadProfile,
    canReadReports,
    canReadUsers,
    canReadAdminReports,
    canReadBoscaAdminReports,
  } = useAvailableActions();

  const isActionsLoading = useSelector(
    (state) => state.app.availableActions?.length === 0 || !state.app.reportKeysLoaded,
  );

  const routes = useRoutes([
    {
      element: (
        <DashboardLayout>
          <Suspense>
            {isActionsLoading ? (
              <SuspenseDiv />
            ) : (
              <Outlet />
            )}
          </Suspense>
        </DashboardLayout>
      ),
      children: [
        { element: <IndexPage />, index: true },
        {
          path: 'sites',
          element: canReadSites ? <SitesPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/:id',
          element: canReadSites ? <SitePage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/skillko-settings/:siteId',
          element: canReadSites ? <SkillkoSettingsPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/procore-settings/:siteId',
          element: canReadSites ? <ProcoreSettingsPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/face-scanner/:siteId',
          element: canReadSites ? <FaceScannerPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/administrative-settings/:siteId',
          element: canReadSites ? <AdministrativeSiteSettings /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/pipeline-compare/:siteId',
          element:
            canReadSites && canReadBoscaAdminReports ? (
              <SitePipelineComparePage />
            ) : (
              <Navigate to="/404" replace />
            ),
        },
        {
          path: 'site/access-levels/:siteId',
          element: canReadSites ? <AccessLevelDesigner /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/access-level-builder/:siteId',
          element: canReadSites ? <AccessLevelBuilder /> : <Navigate to="/404" replace />,
        },
        {
          path: 'site/import-sync-settings/:siteId',
          element: canReadSites ? <ImportSyncSettings /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profiles/:id',
          element: canReadProfiles ? <ProfilesPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/create/:id',
          element: canCreateProfile ? <CreateProfile /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/create/skillko/:siteId/:skillkoId',
          // Registration is gated inside the form; read-only users can still view Skillko records
          element: canReadProfile ? <CreateSkillkoProfile /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/create/skillko/:siteId/:skillkoId/:teamId',
          element: canReadProfile ? <CreateSkillkoProfile /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/create/sitepass/:siteId/:sitePassId',
          element: canReadProfile ? <CreateSitePassProfile /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/import/skillko',
          element: canReadProfiles ? <ProfilesListSkillkoTableInductionComplete /> : <Navigate to="/404" replace />,
        },
        {
          path: 'profile/edit/:profileId/:siteId',
          // Edit profile component is also responsible for showing profile. Editprofile permission is checked inside
          element: canReadProfile ? <EditProfile /> : <Navigate to="/404" replace />,
        },
        {
          path: 'users',
          element: canReadUsers ? <UsersPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'events/all-events',
          element: canReadEvents ? <EventsPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'event-generator',
          element: canReadSites ? <EventGeneratorPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'event-generator/:siteId',
          element: canReadSites ? <EventGeneratorPage /> : <Navigate to="/404" replace />,
        },
        { path: 'account-settings', element: <AccountSettingsPage /> },
        { path: 'skillko-health-check', element: <SkillkoHealthCheckPage /> },
        { path: 'audit-trail', element: <AuditTrailPage /> },
        { path: 'procore-explorer', element: <ProcoreExplorerPage /> },
        {
          path: 'contracting-companies/combine',
          element: <CombineContractingCompaniesPage />,
        },
        {
          path: 'job-titles/combine',
          element: <CombineJobTitlesPage />,
        },

        //New reports (To be updated with new API)
        {
          path: 'reports/reporting-hub',
          element: canReadReports ? <ReportingHubPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'reports/user-time-and-attendance/:id',
          element: canReadReports ? <UserTimeAndAttendancePage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'reports/company-time-and-attendance/:id',
          element: canReadReports ? <CompanyTimeAndAttendancePage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'tenant-profiles',
          element: canReadProfiles ? <TenantProfilesPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'tenant-profiles/:id',
          element: canReadProfiles ? <TenantProfileDetailsPage /> : <Navigate to="/404" replace />,
        },

        // Visitors (UI-only mock module, Bosca admin only)
        {
          path: 'visitors',
          element: canReadBoscaAdminReports ? <VisitorsPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'visitors/new',
          element: canReadBoscaAdminReports ? <VisitorWizardPage /> : <Navigate to="/404" replace />,
        },
        {
          path: 'visitors/wizard/:visitorId',
          element: canReadBoscaAdminReports ? <VisitorWizardPage /> : <Navigate to="/404" replace />,
        },
      ],
    },
    {
      path: 'overview',
      element: <OverviewPage />,
    },
    {
      path: 'switch-account',
      element: (
        <Navigate 
          to="/" 
          replace 
          state={{ switchAccount: true }} 
        />
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
